import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Typography,
  Tabs,
  Tab,
  List,
  ListItemButton,
  ListItemText,
  TextField,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  CircularProgress,
  Divider,
  FormControlLabel,
  Checkbox,
  Autocomplete,
  useMediaQuery,
  useTheme,
  Tooltip,
  Alert,
} from '@mui/material';
import {
  Send as SendIcon,
  Chat as ChatIcon,
  PersonAdd as PersonAddIcon,
  GroupAdd as GroupAddIcon,
} from '@mui/icons-material';
import { useSearchParams } from 'react-router-dom';
import { io } from 'socket.io-client';
import { toast } from 'react-toastify';
import api from '../../utils/api';
import { getAuthAccessToken } from '../../utils/demoPreviewStorage';
import { getCameraSocketUrl } from '../../utils/cameraSocketUrl';
import { getChatIoOptions } from '../../utils/chatIoOptions';
import { useAuth } from '../../context/AuthContext';

function userLabel(u) {
  if (!u) return '';
  const n = `${u.first_name || ''} ${u.last_name || ''}`.trim();
  return n || u.email || u.id;
}

/** Avoid three duplicate toasts when parallel chat list calls fail */
const silent = { skipErrorToast: true };

function isChatApiMissingError(err) {
  const st = err?.response?.status;
  const msg = err?.response?.data?.message;
  return st === 404 && (msg === 'Endpoint not found' || msg === 'endpoint not found');
}

const ChatHub = () => {
  const theme = useTheme();
  const narrow = useMediaQuery(theme.breakpoints.down('md'));
  const [searchParams, setSearchParams] = useSearchParams();
  const { user: me, hasPermission, isAdmin } = useAuth();
  const dmUserIdFromQuery = searchParams.get('dmUserId');
  const canSend = isAdmin || hasPermission('chat_add') || hasPermission('chat_modify');

  const [mainTab, setMainTab] = useState(0);
  const [directList, setDirectList] = useState([]);
  const [teamConvList, setTeamConvList] = useState([]);
  const [teams, setTeams] = useState([]);
  const [loadingLists, setLoadingLists] = useState(true);
  const [chatApiError, setChatApiError] = useState(null);
  const [activeConv, setActiveConv] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [compose, setCompose] = useState('');
  const [sending, setSending] = useState(false);

  const socketRef = useRef(null);
  const messagesEndRef = useRef(null);
  const listScrollRef = useRef(null);

  const [dmOpen, setDmOpen] = useState(false);
  const [dmSearch, setDmSearch] = useState('');
  const [dmOptions, setDmOptions] = useState([]);
  const [dmLoading, setDmLoading] = useState(false);

  const [teamDlgOpen, setTeamDlgOpen] = useState(false);
  const [teamName, setTeamName] = useState('');
  const [teamDesc, setTeamDesc] = useState('');
  const [officialTeam, setOfficialTeam] = useState(false);
  const [teamMembersPick, setTeamMembersPick] = useState([]);
  const [teamDirOpts, setTeamDirOpts] = useState([]);
  const [creatingTeam, setCreatingTeam] = useState(false);
  const teamDirDebounceRef = useRef(null);

  const [addMemOpen, setAddMemOpen] = useState(false);
  const [addMemTeam, setAddMemTeam] = useState(null);
  const [addMemPick, setAddMemPick] = useState([]);
  const [addMemDirOpts, setAddMemDirOpts] = useState([]);
  const [addMemLoading, setAddMemLoading] = useState(false);
  const addMemDebounceRef = useRef(null);

  const teamMemberOptions = useMemo(() => {
    const m = new Map();
    teamMembersPick.forEach((u) => m.set(u.id, u));
    teamDirOpts.forEach((u) => m.set(u.id, u));
    return [...m.values()];
  }, [teamMembersPick, teamDirOpts]);

  const addMemberOptions = useMemo(() => {
    const m = new Map();
    addMemPick.forEach((u) => m.set(u.id, u));
    addMemDirOpts.forEach((u) => m.set(u.id, u));
    return [...m.values()];
  }, [addMemPick, addMemDirOpts]);

  const fetchDirect = useCallback(async () => {
    const r = await api.get('/chat/conversations', {
      params: { filter: 'direct' },
      ...silent,
    });
    setDirectList(r.data.data || []);
  }, []);

  const fetchTeamConvs = useCallback(async () => {
    const r = await api.get('/chat/conversations', {
      params: { filter: 'team' },
      ...silent,
    });
    setTeamConvList(r.data.data || []);
  }, []);

  const fetchTeams = useCallback(async () => {
    const r = await api.get('/chat/teams', { ...silent });
    setTeams(r.data.data || []);
  }, []);

  const refreshAllLists = useCallback(async () => {
    setLoadingLists(true);
    setChatApiError(null);
    const results = await Promise.allSettled([fetchDirect(), fetchTeamConvs(), fetchTeams()]);
    const failed = results.find((x) => x.status === 'rejected');
    if (failed?.reason && isChatApiMissingError(failed.reason)) {
      setChatApiError({
        type: 'missing',
        path: failed.reason?.response?.data?.path,
        hint: failed.reason?.response?.data?.hint,
      });
      toast.error('Chat API is not available on this server.');
    } else if (failed?.reason) {
      const msg = failed.reason?.response?.data?.message || 'Failed to load chat lists';
      setChatApiError({ type: 'other', message: msg });
      toast.error(msg);
    }
    setLoadingLists(false);
  }, [fetchDirect, fetchTeamConvs, fetchTeams]);

  useEffect(() => {
    refreshAllLists();
  }, [refreshAllLists]);

  useEffect(() => {
    if (!dmUserIdFromQuery || !me?.id) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const r = await api.post('/chat/conversations/direct', { user_id: dmUserIdFromQuery }, silent);
        if (cancelled) return;
        setSearchParams(
          (prev) => {
            const next = new URLSearchParams(prev);
            next.delete('dmUserId');
            return next;
          },
          { replace: true }
        );
        setMainTab(0);
        await fetchDirect();
        setActiveConv(r.data.data);
      } catch (e) {
        if (!cancelled) {
          toast.error(e.response?.data?.message || 'Could not open chat');
          setSearchParams(
            (prev) => {
              const next = new URLSearchParams(prev);
              next.delete('dmUserId');
              return next;
            },
            { replace: true }
          );
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [dmUserIdFromQuery, me?.id, fetchDirect, setSearchParams]);

  useEffect(() => {
    const token = getAuthAccessToken();
    if (!token) return undefined;
    const s = io(getCameraSocketUrl(), getChatIoOptions(token));
    socketRef.current = s;
    s.on('chat:message', (payload) => {
      if (!payload?.conversation_id) return;
      setMessages((prev) => {
        if (prev.some((m) => m.id === payload.id)) return prev;
        return [...prev, payload];
      });
      setDirectList((list) =>
        list.map((c) =>
          c.id === payload.conversation_id
            ? {
                ...c,
                last_message_preview: payload.body?.slice(0, 200),
                last_message_at: payload.created_at,
              }
            : c
        )
      );
      setTeamConvList((list) =>
        list.map((c) =>
          c.id === payload.conversation_id
            ? {
                ...c,
                last_message_preview: payload.body?.slice(0, 200),
                last_message_at: payload.created_at,
              }
            : c
        )
      );
    });
    return () => {
      s.removeAllListeners();
      s.disconnect();
      socketRef.current = null;
    };
  }, []);

  const prevConvRef = useRef(null);
  useEffect(() => {
    const s = socketRef.current;
    const prev = prevConvRef.current;
    if (prev && s?.connected) {
      s.emit('chat:leave', { conversationId: prev });
    }
    prevConvRef.current = activeConv?.id || null;
    if (activeConv?.id && s?.connected) {
      s.emit('chat:join', { conversationId: activeConv.id });
      api.post(`/chat/conversations/${activeConv.id}/read`, {}, silent).catch(() => {});
    }
  }, [activeConv?.id]);

  const loadMessages = useCallback(async (convId) => {
    if (!convId) return;
    setLoadingMessages(true);
    try {
      const r = await api.get(`/chat/conversations/${convId}/messages`, {
        params: { page: 1, limit: 100 },
        ...silent,
      });
      setMessages(r.data.data || []);
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed to load messages');
      setMessages([]);
    } finally {
      setLoadingMessages(false);
    }
  }, []);

  useEffect(() => {
    if (activeConv?.id) loadMessages(activeConv.id);
    else setMessages([]);
  }, [activeConv?.id, loadMessages]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, activeConv?.id]);

  const openConversation = (row) => {
    setActiveConv(row);
  };

  const currentList =
    mainTab === 0 ? directList : mainTab === 1 ? teamConvList : [];

  const handleSend = async () => {
    const text = compose.trim();
    if (!text || !activeConv?.id || !canSend) return;
    setSending(true);
    try {
      const r = await api.post(`/chat/conversations/${activeConv.id}/messages`, { body: text }, silent);
      const payload = r.data.data;
      setCompose('');
      setMessages((prev) => (prev.some((m) => m.id === payload.id) ? prev : [...prev, payload]));
      await refreshAllLists();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Send failed');
    } finally {
      setSending(false);
    }
  };

  const searchDirectory = useCallback(async (q) => {
    setDmLoading(true);
    try {
      const r = await api.get('/chat/directory', { params: { search: q, limit: 25 }, ...silent });
      setDmOptions(r.data.data || []);
    } catch {
      setDmOptions([]);
    } finally {
      setDmLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!dmOpen) return undefined;
    const t = setTimeout(() => searchDirectory(dmSearch), 300);
    return () => clearTimeout(t);
  }, [dmOpen, dmSearch, searchDirectory]);

  const startDm = async (other) => {
    if (!other?.id) return;
    try {
      const r = await api.post('/chat/conversations/direct', { user_id: other.id }, silent);
      const row = r.data.data;
      setDmOpen(false);
      setDmSearch('');
      setMainTab(0);
      await fetchDirect();
      setActiveConv(row);
    } catch (e) {
      toast.error(e.response?.data?.message || 'Could not open chat');
    }
  };

  const submitNewTeam = async () => {
    const name = teamName.trim();
    if (name.length < 2) {
      toast.error('Team name is required');
      return;
    }
    setCreatingTeam(true);
    try {
      const member_user_ids = teamMembersPick.map((u) => u.id);
      const body = {
        name,
        description: teamDesc.trim() || undefined,
        member_user_ids,
        ...(isAdmin && officialTeam ? { team_source: 'admin' } : {}),
      };
      const r = await api.post('/chat/teams', body, silent);
      const payload = r.data.data || {};
      const cid = payload.conversation_id;
      const createdTeam = payload.team;
      toast.success('Team created');
      setTeamDlgOpen(false);
      setTeamName('');
      setTeamDesc('');
      setOfficialTeam(false);
      setTeamMembersPick([]);
      setTeamDirOpts([]);
      await refreshAllLists();
      if (cid) {
        const src = createdTeam?.team_source || (officialTeam && isAdmin ? 'admin' : 'user');
        setActiveConv({
          id: cid,
          type: 'team',
          title: createdTeam?.name || name,
          subtitle: src === 'admin' ? 'Official team' : 'Team',
          team: createdTeam || { name, team_source: src },
        });
        setMainTab(1);
      } else {
        setMainTab(2);
      }
    } catch (e) {
      toast.error(e.response?.data?.message || 'Create team failed');
    } finally {
      setCreatingTeam(false);
    }
  };

  const openTeamChat = (t) => {
    if (!t.conversation_id) {
      toast.error('No conversation for this team yet');
      return;
    }
    setMainTab(1);
    setActiveConv({
      id: t.conversation_id,
      type: 'team',
      title: t.name,
      subtitle: t.team_source === 'admin' ? 'Official team' : 'Team',
      team: t,
    });
  };

  const submitAddMembers = async () => {
    if (!addMemTeam?.id || !addMemPick.length) return;
    setAddMemLoading(true);
    try {
      await api.post(
        `/chat/teams/${addMemTeam.id}/members`,
        { user_ids: addMemPick.map((u) => u.id) },
        silent
      );
      toast.success('Members added');
      setAddMemOpen(false);
      setAddMemTeam(null);
      setAddMemPick([]);
      await refreshAllLists();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed to add members');
    } finally {
      setAddMemLoading(false);
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 120px)', minHeight: 480 }}>
      {chatApiError?.type === 'missing' && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          <Typography variant="body2" sx={{ mb: 1 }}>
            The app reached the API, but <strong>/api/chat</strong> is not registered (common if the backend image is
            older than the Chat feature).
          </Typography>
          <Typography variant="body2" component="div" sx={{ mb: 1 }}>
            Fix: build backend with <code>Dockerfile.prod</code>, use a <strong>new image tag</strong>, run{' '}
            <code>deployments/kubernetes/terraform/docker-local/apply-backend-k8s-image.sh &lt;tag&gt;</code>, then
            hard-refresh. Test <code>GET …/api/chat</code> with your <strong>Bearer token</strong> (same as the app); it
            must return JSON, not “Endpoint not found”.
          </Typography>
          {chatApiError.path && (
            <Typography variant="caption" color="text.secondary" display="block">
              Server said: {chatApiError.path}
              {chatApiError.hint ? ` — ${chatApiError.hint}` : ''}
            </Typography>
          )}
        </Alert>
      )}
      {chatApiError?.type === 'other' && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {chatApiError.message}
        </Alert>
      )}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, flexWrap: 'wrap' }}>
        <ChatIcon color="primary" />
        <Typography variant="h5" sx={{ fontWeight: 600 }}>
          Chat
        </Typography>
        <Box sx={{ flex: 1 }} />
        {mainTab === 0 && (
          <Tooltip title="Message someone">
            <Button startIcon={<PersonAddIcon />} variant="outlined" size="small" onClick={() => setDmOpen(true)}>
              New chat
            </Button>
          </Tooltip>
        )}
        {mainTab === 2 && (
          <>
            <Button
              startIcon={<GroupAddIcon />}
              variant="contained"
              size="small"
              onClick={() => setTeamDlgOpen(true)}
              disabled={!canSend}
            >
              Create team
            </Button>
          </>
        )}
      </Box>

      <Tabs value={mainTab} onChange={(_, v) => setMainTab(v)} sx={{ borderBottom: 1, borderColor: 'divider', mb: 1 }}>
        <Tab label="My chats" />
        <Tab label="Team chats" />
        <Tab label="Teams" />
      </Tabs>

      <Box sx={{ display: 'flex', flex: 1, minHeight: 0, gap: 2, flexDirection: narrow ? 'column' : 'row' }}>
        <Box
          ref={listScrollRef}
          sx={{
            width: narrow ? '100%' : 320,
            maxHeight: narrow ? 220 : 'none',
            flexShrink: 0,
            border: 1,
            borderColor: 'divider',
            borderRadius: 1,
            overflow: 'auto',
            bgcolor: 'background.paper',
          }}
        >
          {loadingLists ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress size={28} />
            </Box>
          ) : mainTab === 2 ? (
            <List dense disablePadding>
              {teams.length === 0 && (
                <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
                  You are not in any teams yet. Create one or ask an admin to add you.
                </Typography>
              )}
              {teams.map((t) => (
                <React.Fragment key={t.id}>
                  <ListItemButton
                    onClick={() => openTeamChat(t)}
                    selected={activeConv?.id === t.conversation_id}
                    alignItems="flex-start"
                  >
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                          <Typography variant="subtitle2">{t.name}</Typography>
                          <Chip
                            size="small"
                            label={t.team_source === 'admin' ? 'Official' : 'My team'}
                            color={t.team_source === 'admin' ? 'primary' : 'default'}
                            variant="outlined"
                          />
                          {t.my_role === 'owner' && <Chip size="small" label="Owner" />}
                        </Box>
                      }
                      secondary={t.description || 'Team chat'}
                    />
                  </ListItemButton>
                  {(isAdmin || t.my_role === 'owner') && canSend && (
                    <Box sx={{ px: 2, pb: 1 }}>
                      <Button
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          setAddMemTeam(t);
                          setAddMemOpen(true);
                        }}
                      >
                        Add members
                      </Button>
                    </Box>
                  )}
                  <Divider component="li" />
                </React.Fragment>
              ))}
            </List>
          ) : currentList.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
              {mainTab === 0 ? 'No direct messages. Start a new chat.' : 'No team conversations yet.'}
            </Typography>
          ) : (
            <List dense disablePadding>
              {currentList.map((c) => (
                <ListItemButton
                  key={c.id}
                  selected={activeConv?.id === c.id}
                  onClick={() => openConversation(c)}
                  alignItems="flex-start"
                >
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}>
                        <Typography variant="subtitle2" noWrap>
                          {c.title}
                        </Typography>
                        {c.unread_count > 0 && (
                          <Chip label={c.unread_count} size="small" color="error" sx={{ height: 22 }} />
                        )}
                      </Box>
                    }
                    secondary={c.last_message_preview || c.subtitle || '—'}
                  />
                </ListItemButton>
              ))}
            </List>
          )}
        </Box>

        <Box
          sx={{
            flex: 1,
            minHeight: narrow ? 360 : 0,
            display: 'flex',
            flexDirection: 'column',
            border: 1,
            borderColor: 'divider',
            borderRadius: 1,
            bgcolor: 'background.paper',
            overflow: 'hidden',
          }}
        >
          {!activeConv ? (
            <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', p: 3 }}>
              <Typography color="text.secondary">Select a conversation</Typography>
            </Box>
          ) : (
            <>
              <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
                <Typography variant="subtitle1" fontWeight={600}>
                  {activeConv.title}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {activeConv.subtitle}
                </Typography>
              </Box>
              <Box sx={{ flex: 1, overflow: 'auto', p: 2, bgcolor: (t) => t.palette.action.hover }}>
                {loadingMessages ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                    <CircularProgress size={28} />
                  </Box>
                ) : (
                  messages.map((m) => {
                    const mine = m.sender_user_id === me?.id;
                    const who = mine
                      ? 'You'
                      : userLabel(m.sender) || (m.sender?.email ?? 'User');
                    return (
                      <Box
                        key={m.id}
                        sx={{
                          mb: 1.5,
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: mine ? 'flex-end' : 'flex-start',
                        }}
                      >
                        <Typography variant="caption" color="text.secondary" sx={{ px: 0.5 }}>
                          {who} · {m.created_at ? new Date(m.created_at).toLocaleString() : ''}
                        </Typography>
                        <Box
                          sx={{
                            maxWidth: '85%',
                            px: 1.5,
                            py: 1,
                            borderRadius: 2,
                            bgcolor: mine ? 'primary.main' : 'background.paper',
                            color: mine ? 'primary.contrastText' : 'text.primary',
                            border: mine ? 0 : 1,
                            borderColor: 'divider',
                            whiteSpace: 'pre-wrap',
                            wordBreak: 'break-word',
                          }}
                        >
                          {m.body}
                        </Box>
                      </Box>
                    );
                  })
                )}
                <div ref={messagesEndRef} />
              </Box>
              <Box sx={{ p: 2, borderTop: 1, borderColor: 'divider', display: 'flex', gap: 1 }}>
                <TextField
                  fullWidth
                  size="small"
                  placeholder={canSend ? 'Type a message…' : 'You do not have permission to send messages'}
                  value={compose}
                  onChange={(e) => setCompose(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      handleSend();
                    }
                  }}
                  disabled={!canSend}
                  multiline
                  maxRows={4}
                />
                <IconButton color="primary" onClick={handleSend} disabled={!canSend || sending || !compose.trim()}>
                  {sending ? <CircularProgress size={22} /> : <SendIcon />}
                </IconButton>
              </Box>
            </>
          )}
        </Box>
      </Box>

      <Dialog open={dmOpen} onClose={() => setDmOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>New direct message</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            autoFocus
            margin="dense"
            label="Search people"
            value={dmSearch}
            onChange={(e) => setDmSearch(e.target.value)}
            helperText="Search by name or email"
          />
          {dmLoading ? (
            <CircularProgress size={24} sx={{ mt: 2 }} />
          ) : (
            <List dense>
              {dmOptions.map((u) => (
                <ListItemButton key={u.id} onClick={() => startDm(u)}>
                  <ListItemText primary={userLabel(u)} secondary={u.email} />
                </ListItemButton>
              ))}
            </List>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDmOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={teamDlgOpen}
        onClose={() => !creatingTeam && setTeamDlgOpen(false)}
        maxWidth="sm"
        fullWidth
        TransitionProps={{
          onExited: () => {
            setTeamDirOpts([]);
          },
        }}
      >
        <DialogTitle>Create team</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            margin="dense"
            label="Team name"
            value={teamName}
            onChange={(e) => setTeamName(e.target.value)}
          />
          <TextField
            fullWidth
            margin="dense"
            label="Description (optional)"
            value={teamDesc}
            onChange={(e) => setTeamDesc(e.target.value)}
            multiline
            minRows={2}
          />
          {isAdmin && (
            <FormControlLabel
              control={
                <Checkbox checked={officialTeam} onChange={(e) => setOfficialTeam(e.target.checked)} />
              }
              label="Official team (admin)"
            />
          )}
          <Autocomplete
            multiple
            sx={{ mt: 1 }}
            options={teamMemberOptions}
            value={teamMembersPick}
            onChange={(_, v) => setTeamMembersPick(v)}
            getOptionLabel={(o) => userLabel(o)}
            isOptionEqualToValue={(a, b) => a.id === b.id}
            filterOptions={(x) => x}
            onInputChange={(_, v, reason) => {
              if (reason !== 'input') return;
              if (teamDirDebounceRef.current) clearTimeout(teamDirDebounceRef.current);
              const q = v.trim();
              if (q.length < 1) {
                setTeamDirOpts([]);
                return;
              }
              teamDirDebounceRef.current = setTimeout(async () => {
                try {
                  const res = await api.get('/chat/directory', {
                    params: { search: q, limit: 20 },
                    ...silent,
                  });
                  setTeamDirOpts(res.data.data || []);
                } catch {
                  setTeamDirOpts([]);
                }
              }, 250);
            }}
            renderInput={(params) => <TextField {...params} label="Add members (search)" />}
          />
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
            Type in the members field to search users, then select from the dropdown.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTeamDlgOpen(false)} disabled={creatingTeam}>
            Cancel
          </Button>
          <Button variant="contained" onClick={submitNewTeam} disabled={creatingTeam}>
            Create
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={addMemOpen}
        onClose={() => !addMemLoading && setAddMemOpen(false)}
        maxWidth="sm"
        fullWidth
        TransitionProps={{
          onExited: () => {
            setAddMemDirOpts([]);
            setAddMemPick([]);
          },
        }}
      >
        <DialogTitle>Add members — {addMemTeam?.name}</DialogTitle>
        <DialogContent>
          <Autocomplete
            multiple
            options={addMemberOptions}
            value={addMemPick}
            onChange={(_, v) => setAddMemPick(v)}
            getOptionLabel={(o) => userLabel(o)}
            isOptionEqualToValue={(a, b) => a.id === b.id}
            filterOptions={(x) => x}
            onInputChange={(_, v, reason) => {
              if (reason !== 'input') return;
              if (addMemDebounceRef.current) clearTimeout(addMemDebounceRef.current);
              const q = v.trim();
              if (q.length < 1) {
                setAddMemDirOpts([]);
                return;
              }
              addMemDebounceRef.current = setTimeout(async () => {
                try {
                  const res = await api.get('/chat/directory', {
                    params: { search: q, limit: 20 },
                    ...silent,
                  });
                  setAddMemDirOpts(res.data.data || []);
                } catch {
                  setAddMemDirOpts([]);
                }
              }, 250);
            }}
            renderInput={(params) => <TextField {...params} label="Search users" margin="dense" />}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddMemOpen(false)} disabled={addMemLoading}>
            Cancel
          </Button>
          <Button variant="contained" onClick={submitAddMembers} disabled={addMemLoading || !addMemPick.length}>
            Add
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ChatHub;
